import { create } from 'zustand'
import api from '../api/api'

export type LearnerCourseAccess = 'OFF' | 'PURCHASED_CATALOG' | 'MARKETPLACE' | 'BOTH'

export type EnrollmentApprovalMode = 'DIRECT' | 'MANAGER_APPROVAL'

interface LearnSettingsState {
  marketplaceEnabled: boolean
  learnerCourseAccess: LearnerCourseAccess
  enrollmentApprovalMode: EnrollmentApprovalMode
  isLoaded: boolean
  isLoading: boolean

  // Actions
  fetchLearnSettings: () => Promise<void>
  refetchLearnSettings: () => Promise<void>
  clear: () => void

  // Helpers
  isMarketplaceSidebarVisible: () => boolean
  isCoursesSidebarVisible: () => boolean
}

export const useLearnSettingsStore = create<LearnSettingsState>()((set, get) => ({
  marketplaceEnabled: false,
  learnerCourseAccess: 'OFF',
  enrollmentApprovalMode: 'DIRECT',
  isLoaded: false,
  isLoading: false,

  fetchLearnSettings: async () => {
    if (get().isLoaded || get().isLoading) return
    set({ isLoading: true })
    try {
      // Read from Core Backend's account_settings.module_settings
      const response = await api.get('/account-settings/module-settings/learn')
      const data = response.data?.data
      set({
        marketplaceEnabled: data?.marketplace_enabled ?? false,
        learnerCourseAccess: data?.learner_course_access ?? 'OFF',
        enrollmentApprovalMode: data?.enrollment_approval_mode ?? 'DIRECT',
        isLoaded: true,
        isLoading: false,
      })
    } catch {
      set({
        marketplaceEnabled: false,
        learnerCourseAccess: 'OFF',
        isLoaded: true,
        isLoading: false,
      })
    }
  },

  refetchLearnSettings: async () => {
    set({ isLoaded: false, isLoading: false })
    const { fetchLearnSettings } = get()
    await fetchLearnSettings()
  },

  clear: () =>
    set({
      marketplaceEnabled: false,
      learnerCourseAccess: 'OFF',
      enrollmentApprovalMode: 'DIRECT',
      isLoaded: false,
      isLoading: false,
    }),

  isMarketplaceSidebarVisible: () => {
    const { marketplaceEnabled, learnerCourseAccess } = get()
    // Learners see marketplace only when marketplace is enabled AND "Both" is selected
    return marketplaceEnabled && learnerCourseAccess === 'BOTH'
  },

  isCoursesSidebarVisible: () => {
    const { learnerCourseAccess } = get()
    return learnerCourseAccess === 'PURCHASED_CATALOG' || learnerCourseAccess === 'BOTH'
  },
}))
